/**
 * Client-side "Show" filter semantics for the Launches and Groups list tables
 * (tcx-core-platforms-903 Q6).
 *
 * The three hidden lifecycle dimensions are ORTHOGONAL and AND-joined (a row must satisfy ALL of
 * them to be shown), replacing the earlier multiselect whose options were mutually exclusive and
 * therefore yielded empty/confusing results when combined:
 *
 *  1. statusView (single-select, mutually exclusive):
 *       'default'  => any status EXCEPT the hidden buckets (GROUPED / DELETED / RESOLVED / SHIPPED)
 *       'grouped'  => ONLY GROUPED
 *       'deleted'  => ONLY DELETED
 *       'resolved' => ONLY the terminal statuses (RESOLVED or SHIPPED)
 *  2. showArchived (toggle): off => exclude archived; on => include ONLY archived. Archived rows
 *     preserve their workStatus (frozen snapshot), so this composes with statusView.
 *  3. showUnassigned (toggle, tasks only): on => include ONLY rows with no writer assignment.
 *  4. showMineOnly (toggle, tasks only): on => include ONLY rows assigned to the current user (by
 *     alias, case-insensitive). Orthogonal to the others; combining it with showUnassigned yields an
 *     empty set (a row cannot be both unassigned and assigned to you), which is the correct AND result.
 *
 * These are pure so they can be unit-tested without rendering the table.
 */

import { WorkStatus } from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';

export type StatusView = 'default' | 'grouped' | 'deleted' | 'resolved';

// Terminal lifecycle statuses: finished work. Both are terminal per the model
// (AWSDevDocsVirtualSmileyConferenceToolModel type.smithy WorkStatus), and the backend can set
// SHIPPED, so it must be treated the same as RESOLVED everywhere the terminal state matters —
// otherwise a SHIPPED row leaks into the default "Active" view and is invisible under "Resolved"
// (finding on CR-301380769). Derived from the generated WorkStatus enum rather than bare string
// literals so a model rename/re-case fails the build instead of silently no-matching (CR-301380769
// rev5). Typed Set<string> so `.has(workStatus ?? '')` stays valid against the raw string input.
// Exported so GroupDetail's isTerminal check reuses the same set rather than re-listing the statuses
// and drifting.
export const TERMINAL_STATUSES: Set<string> = new Set([
  WorkStatus.RESOLVED,
  WorkStatus.SHIPPED,
]);

// Statuses hidden from the default view because they each have their own dedicated bucket/view.
const HIDDEN_BUCKETS: Set<string> = new Set([
  WorkStatus.GROUPED,
  WorkStatus.DELETED,
  ...TERMINAL_STATUSES,
]);

/** True if a row's workStatus passes the selected single-select view. */
export function matchesStatusView(
  workStatus: string | undefined,
  view: StatusView
): boolean {
  switch (view) {
    case 'grouped':
      return workStatus === WorkStatus.GROUPED;
    case 'deleted':
      return workStatus === WorkStatus.DELETED;
    case 'resolved':
      // The Resolved view shows every terminal status (RESOLVED and SHIPPED), not just RESOLVED.
      return TERMINAL_STATUSES.has(workStatus ?? '');
    default:
      // Default view hides the buckets that have their own dedicated view.
      return !HIDDEN_BUCKETS.has(workStatus ?? '');
  }
}

/** True if a row passes the orthogonal Archived toggle (off => only unarchived; on => only archived). */
export function matchesArchived(
  archived: boolean | undefined,
  showArchived: boolean
): boolean {
  return showArchived ? archived === true : archived !== true;
}

/**
 * True if a row passes the orthogonal "Assigned to me" toggle. Off => always passes. On => the row's
 * assignee alias must be present and case-insensitively equal to the current user's alias. When the
 * current user's alias is unknown (e.g. unauthenticated), nothing matches.
 */
export function matchesAssignedToMe(
  assigneeAlias: string | null | undefined,
  currentUserAlias: string | null | undefined,
  showMineOnly: boolean
): boolean {
  if (!showMineOnly) return true;
  if (!assigneeAlias || !currentUserAlias) return false;
  return assigneeAlias.toLowerCase() === currentUserAlias.toLowerCase();
}

/**
 * Combined predicate for the Launch Tasks table: statusView AND Archived AND (optionally) Unassigned
 * AND (optionally) Assigned-to-me. `hasAssignee` is whether the row has a writer assignment.
 */
export function matchesTaskFilters(args: {
  workStatus: string | undefined;
  archived: boolean | undefined;
  hasAssignee: boolean;
  view: StatusView;
  showArchived: boolean;
  showUnassigned: boolean;
  assigneeAlias?: string | null;
  currentUserAlias?: string | null;
  showMineOnly?: boolean;
}): boolean {
  if (!matchesStatusView(args.workStatus, args.view)) return false;
  if (!matchesArchived(args.archived, args.showArchived)) return false;
  if (args.showUnassigned && args.hasAssignee) return false;
  if (
    !matchesAssignedToMe(
      args.assigneeAlias,
      args.currentUserAlias,
      args.showMineOnly ?? false
    )
  ) {
    return false;
  }
  return true;
}

/**
 * Combined predicate for the Groups table: groups can only be RESOLVED or not, so the view is
 * 'default' | 'resolved'; there is no Unassigned dimension. statusView AND Archived.
 */
export function matchesGroupFilters(args: {
  workStatus: string | undefined;
  archived: boolean | undefined;
  view: 'default' | 'resolved';
  showArchived: boolean;
}): boolean {
  if (!matchesStatusView(args.workStatus, args.view)) return false;
  if (!matchesArchived(args.archived, args.showArchived)) return false;
  return true;
}
